import type { knowledge } from '@cwos/api-client';
import type { PillVariant } from './libraryLogic.js';

/**
 * Pure view logic for the ARTICLES half of the Library — the written policies a
 * person authors, versions, publishes and is asked to acknowledge.
 *
 * No DOM, no react-query, no `@cwos/ui-admin`, for the same reason
 * `libraryLogic.ts` has none: every judgement below is a sentence somebody acts
 * on, and a sentence somebody acts on has to be assertable without mounting a
 * screen.
 *
 * ===========================================================================
 * THE ONE THING THIS FILE EXISTS TO KEEP APART
 * ===========================================================================
 * "You have not acknowledged this" and "Phibian did not ask" are different
 * answers and they look identical if you store the ack as a `boolean`. The
 * acknowledgement question has FIVE answers on this surface and
 * {@link ackState} returns all five:
 *
 *   not-published  the article has no published version, so there is nothing to
 *                  acknowledge. ABSENT — not "outstanding".
 *   refused        the server said 403. WITHHELD — the reader may not be told.
 *   unknown        the read has not returned, or it failed. WE DID NOT LOOK.
 *   acknowledged   a real measured yes.
 *   outstanding    a real measured no, and the only one that is a to-do.
 *
 * A screen that renders `outstanding` for the first three tells a worker they
 * owe an attestation they cannot give, or — far worse on the compliance side —
 * lets a supervisor read a failed request as "this person has not signed".
 */

// ---------------------------------------------------------------------------
// Article status
// ---------------------------------------------------------------------------

/**
 * `knowledge_article_status_ck`, in operator language.
 *
 * A total function with a `default:` returning the stored value, matching
 * `libraryLogic.ts`: a word a future migration adds to the CHECK should read as
 * an unfamiliar word, never as a blank. A blank is indistinguishable from "this
 * article has no status", which is a state that cannot exist.
 */
export function articleStatusLabel(status: knowledge.KnowledgeArticleStatus | string): string {
  switch (status) {
    case 'draft':
      return 'Draft';
    case 'published':
      return 'Published';
    case 'archived':
      return 'Withdrawn';
    default:
      return status;
  }
}

export function articleStatusVariant(
  status: knowledge.KnowledgeArticleStatus | string,
): PillVariant {
  switch (status) {
    case 'published':
      return 'good';
    case 'draft':
      return 'unsure';
    case 'archived':
      return 'plain';
    default:
      return 'plain';
  }
}

/**
 * What the status MEANS for a reader, which the word alone does not carry.
 *
 * "Draft" reads as "nearly done" to an author and as nothing at all to a
 * worker. What matters is who can see it, and that is the sentence.
 */
export function articleStatusSentence(status: knowledge.KnowledgeArticleStatus | string): string {
  switch (status) {
    case 'draft':
      return 'Only people who author policies can see this. Nobody has been asked to read it.';
    case 'published':
      return 'This is what your people are served when they open this policy.';
    case 'archived':
      return 'Withdrawn. Readers are no longer served it — and every version, attachment and acknowledgement it collected is kept.';
    default:
      return 'Phibian does not recognise this status and is showing it as stored.';
  }
}

/**
 * Who an article is targeted at.
 *
 * `null` is NOT "nobody" and not a missing value — it is the widest possible
 * targeting, and rendering it as an empty cell says the opposite of what it
 * means. Compare `libraryLogic.narrowings`, which refuses to list an empty
 * array for exactly the same reason.
 */
export function audienceSentence(article: {
  audience: string | null;
  programId: string | null;
}): string {
  if (article.audience === null && article.programId === null) {
    return 'Everyone in your organization who can open the Library.';
  }
  if (article.audience === null) {
    return 'Everyone working in one program.';
  }
  if (article.programId === null) {
    return `Anyone whose role is “${article.audience}”.`;
  }
  return `Anyone whose role is “${article.audience}”, working in one program.`;
}

// ---------------------------------------------------------------------------
// Acknowledgement — the five-answer question
// ---------------------------------------------------------------------------

export type AckState =
  /** No published version exists. There is nothing to acknowledge. */
  | { kind: 'not-published'; label: string; detail: string }
  /** The server refused the read. Withheld, never "no". */
  | { kind: 'refused'; label: string; detail: string }
  /** Not back yet, or it failed. We did not look. */
  | { kind: 'unknown'; label: string; detail: string }
  | { kind: 'acknowledged'; label: string; detail: string }
  | { kind: 'outstanding'; label: string; detail: string };

/**
 * @param currentVersionId the article's published version, or `null`
 * @param answer           `true`/`false` once the server answered, `undefined`
 *                         while it has not
 * @param refused          the read came back 403
 * @param failed           the read came back some other error
 */
export function ackState(
  currentVersionId: string | null,
  answer: boolean | undefined,
  refused = false,
  failed = false,
): AckState {
  if (currentVersionId === null) {
    return {
      kind: 'not-published',
      label: 'Nothing to acknowledge yet',
      detail:
        'This policy has no published version. Acknowledgement is bound to a version, so there ' +
        'is nothing here for anyone to sign — this is not an outstanding attestation.',
    };
  }
  // REFUSED BEFORE FAILED, and both before the answer: a 403 is an answer about
  // the reader and it must not be reported as an unanswered question, and an
  // unanswered question must not be reported as "no".
  if (refused) {
    return {
      kind: 'refused',
      label: 'Not shown to you',
      detail:
        'Whether you have acknowledged this is not something this account may be told. It is ' +
        'withheld, not a “no”.',
    };
  }
  if (failed || answer === undefined) {
    return {
      kind: 'unknown',
      label: 'Not known',
      detail:
        'Phibian could not check whether you have acknowledged this. Treat it as unknown rather ' +
        'than as unsigned — it is not a statement that you have not read it.',
    };
  }
  if (answer) {
    return {
      kind: 'acknowledged',
      label: 'You have acknowledged this version',
      detail:
        'Your acknowledgement is bound to this exact version. When a newer version is published ' +
        'you will be asked again, because you would be signing for different words.',
    };
  }
  return {
    kind: 'outstanding',
    label: 'You have not acknowledged this version',
    detail:
      'Acknowledging records that you have read this exact version, with your name and the ' +
      'moment. It does not change the policy.',
  };
}

// ---------------------------------------------------------------------------
// The acknowledgement ledger — policy-attestation evidence
// ---------------------------------------------------------------------------

/**
 * What the ledger panel says. THE COMPLIANCE SURFACE, and the one most likely
 * to be read as a fact about people when it is a fact about the reader.
 *
 *   refused   the caller does not hold the managing key. NOBODY WAS COUNTED.
 *   unasked   there is no published version to count acknowledgements of.
 *   pending   the read is in flight.
 *   failed    it did not come back.
 *   none      a measured zero — the version is published and nobody has signed.
 *   some      n people have.
 *
 * `none` and `refused` are the pair this exists for. An attestation report that
 * prints "0 of your staff have acknowledged the abuse-reporting policy" when in
 * fact it never asked is worse than no report, because somebody acts on it.
 */
export type LedgerState =
  | { kind: 'refused'; label: string; detail: string }
  | { kind: 'unasked'; label: string; detail: string }
  | { kind: 'pending'; label: string; detail: string }
  | { kind: 'failed'; label: string; detail: string }
  | { kind: 'none'; label: string; detail: string }
  | { kind: 'some'; label: string; detail: string; count: number };

export function ledgerState(input: {
  mayRead: boolean;
  currentVersionId: string | null;
  acks: readonly unknown[] | undefined;
  isError: boolean;
  refused: boolean;
}): LedgerState {
  if (!input.mayRead) {
    return {
      kind: 'refused',
      label: 'Not shown to you',
      detail:
        'Reading who has acknowledged a policy needs a permission this account does not hold, so ' +
        'Phibian has not looked. This is not a count of nobody.',
    };
  }
  if (input.currentVersionId === null) {
    return {
      kind: 'unasked',
      label: 'Nobody has been asked',
      detail:
        'This policy has no published version, so no acknowledgement could have been given. ' +
        'Nobody is outstanding.',
    };
  }
  if (input.refused) {
    return {
      kind: 'refused',
      label: 'Not shown to you',
      detail:
        'The server refused this read. Who has acknowledged this version is withheld from this ' +
        'account — it is not a count of nobody.',
    };
  }
  if (input.isError) {
    return {
      kind: 'failed',
      label: 'Not known',
      detail:
        'Phibian could not read the acknowledgement record just now. Do not read this as “nobody ' +
        'has signed” — it is “we could not tell”.',
    };
  }
  if (input.acks === undefined) {
    return {
      kind: 'pending',
      label: 'Reading the record',
      detail: 'Phibian is fetching who has acknowledged this version.',
    };
  }
  if (input.acks.length === 0) {
    return {
      kind: 'none',
      label: 'Nobody yet',
      detail:
        'This version is published and no one has acknowledged it. This is a measured zero: ' +
        'Phibian looked.',
    };
  }
  return {
    kind: 'some',
    label:
      input.acks.length === 1
        ? '1 person has acknowledged this version'
        : `${input.acks.length} people have acknowledged this version`,
    detail:
      'Each entry names one person and the moment they acknowledged this exact version. ' +
      'Acknowledgements are not carried forward from earlier versions.',
    count: input.acks.length,
  };
}

// ---------------------------------------------------------------------------
// Versions
// ---------------------------------------------------------------------------

/**
 * How a version sits relative to what readers are served.
 *
 * "Newer than what is published" is the state an author most needs and the one
 * a bare version list does not show: writing a version does NOT publish it, so
 * an author who edited and left can be looking at their own words while every
 * reader is still being served the previous ones.
 */
export type VersionStanding = 'published' | 'newer-unpublished' | 'earlier';

export function versionStanding(
  version: { id: string; versionNumber: number },
  currentVersionId: string | null,
  currentVersionNumber: number | null,
): VersionStanding {
  if (currentVersionId !== null && version.id === currentVersionId) return 'published';
  if (currentVersionNumber === null) return 'newer-unpublished';
  return version.versionNumber > currentVersionNumber ? 'newer-unpublished' : 'earlier';
}

export function versionStandingLabel(standing: VersionStanding): string {
  switch (standing) {
    case 'published':
      return 'This is what readers see';
    case 'newer-unpublished':
      return 'Written, not published — no reader has been served this';
    case 'earlier':
      return 'An earlier version, kept';
  }
}

/**
 * Whether an author has unpublished work sitting on this article, and the
 * sentence for it.
 *
 * `null` when there is none, so a screen does not print a warning on every
 * article and train people to stop reading warnings.
 */
export function unpublishedDraftNote(
  versions: readonly { id: string; versionNumber: number }[],
  currentVersionId: string | null,
): string | null {
  if (versions.length === 0) return null;
  const current = versions.find((v) => v.id === currentVersionId);
  if (currentVersionId === null) {
    return versions.length === 1
      ? 'One version has been written and none published. Nobody has been served this policy.'
      : `${versions.length} versions have been written and none published. Nobody has been served this policy.`;
  }
  if (!current) return null;
  const newer = versions.filter((v) => v.versionNumber > current.versionNumber).length;
  if (newer === 0) return null;
  return newer === 1
    ? 'A newer version has been written and not published. Your readers are still being served the published one.'
    : `${newer} newer versions have been written and none published. Your readers are still being served the published one.`;
}
